/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySelect as MatSelect, MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { ActivatedRoute, Router } from '@angular/router';
import { FOLIO_DATA_SERVICE, FolioDataService } from '@app/app/page-modules/folio/services/data.service';
import { FolioProject } from '@app/app/page-modules/project/models/project.model';
import { PROJECT_DATA_SERVICE, ProjectDataService } from '@app/app/page-modules/project/services/data.service';
import { LearnerFormAnswer } from '@app/app/page-modules/resource/models';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { RefreshStateAfterFormSubmission, SaveFormAnswer } from '@app/app/page-modules/resource/store/learner-view.actions';
import { FormSavedEvent } from '@app/app/shared/components/resource-preview/form-preview/form-preview-event.model';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { Form, Playlist, ProjectFormContent, TextBoxFormContent, UserPlaylistSubmissionSummary } from '@app/app/shared/models';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { SubmitPlaylistReviewDialogComponent } from 'src/app/page-modules/playlist/shared/view/playlist-action-bar/submit-dialog/submit-dialog.component';
import { RefreshUserPlaylistSubmissionSummary } from 'src/app/page-modules/playlist/store/view/playlist-view.state.actions';
import { LEARNER_VIEW_DATA_SERVICE, LearnerViewDataService } from 'src/app/page-modules/resource/services/data.service';
import { DialogService } from 'src/app/shared/helpers/dialog/dialog.service';

@Component({
  selector: 'ptl-form-preview-project',
  templateUrl: './form-project-preview.component.html',
  styleUrls: ['./form-project-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectFormPreviewComponent implements OnInit, OnChanges {
  @Input() formData: Form<TextBoxFormContent>;
  @Input() learnerFormAnswer: LearnerFormAnswer;
  @Input() userPlaylistSubmissionSummary: UserPlaylistSubmissionSummary;
  @Input() isProjectResource: boolean;
  @Input() playlistUri: string;
  @Input() playlistUid: string;
  @Input() playlistTitle: string;
  @Input() resourceUri: string;
  @Input() resourceUid: string;
  @Input() publisherUri: string;
  @Input() packageUri: string;
  @Input() pageUri: string;
  @Input() languageCode: string;

  @ViewChild('projectSelect') projectSelect!: MatSelect;

  existingUserAnswer: boolean;
  submitted = false;
  submittedOn: Date = null;
  canEdit = false;
  canNotEditReason: string;
  userAnswer: string | null = null;
  requiresResubmission = false;
  submissionInProgress = false;
  validationInProgress = false;
  validationProcessed = false;
  projectsFetched = false;
  projects: FolioProject[] = [];
  projectDetails: Playlist | null = null;
  projectDetailsFetchInProgress = false;
  publicFolioId: string;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    @Inject(LEARNER_VIEW_DATA_SERVICE) private dataService: LearnerViewDataService,
    @Inject(FOLIO_DATA_SERVICE) private folioService: FolioDataService,
    @Inject(PROJECT_DATA_SERVICE) private projectService: ProjectDataService,
  ) {}

  ngOnInit() {
    if (this.formData && (this.formData.content as ProjectFormContent).userAnswer) {
      const content = this.formData.content as ProjectFormContent;
      this.submitted = content.submitted;
      if (content.userAnswer.length > 0) {
        this.existingUserAnswer = true;
        this.userAnswer = content.userAnswer;
        this.loadUserAnswerProjectDetails();
      }
    }
    this.submittedOn = this.learnerFormAnswer?.submittedOn;
    this.checkSubmissionMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.playlistUid && this.resourceUid && this.formData?.uid) {
      this.checkSubmissionMode();
    } else {
      if (changes.learnerFormAnswer || changes.userPlaylistSubmissionSummary) {
        this.checkSubmissionMode();
      }
    }
  }

  redirectToProjectCreate(): void {
    this.folioService.getUserFolio().subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        RedirectHelper.redirectByUrl(
          this.ngZone,
          this.router,
          this.activatedRoute,
          `/folio/${value.publicId}/projects/create`,
          null,
          '_blank',
        );
      }
    });
  }

  fetchProjects(): void {
    this.folioService.getFolioProjects(0, 100).subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        this.projects = value.content;
      }
      this.projectsFetched = true;
      this.cd.detectChanges();
      if (!this.projectSelect.panelOpen) {
        this.projectSelect.open();
      }
    });
  }

  onProjectSelected(event: MatSelectChange) {
    const selectedProjectUri = event.value;
    if (!selectedProjectUri) throw new Error('Selected project is not set');

    const savedEvent: FormSavedEvent = {
      formUid: this.formData.uid,
      answer: selectedProjectUri,
      submitted: this.submitted,
      submittedOn: this.submittedOn,
    };
    this.existingUserAnswer = true;
    this.projectDetails = this.projects.find((p) => p.project.uri === selectedProjectUri)?.project;
    this.store.dispatch(new SaveFormAnswer(this.playlistUid, savedEvent));
  }

  submit() {
    if (ContentHelper.isProgressiveSubmissionMode(this.userPlaylistSubmissionSummary)) {
      const dialogMessage =
        this.userPlaylistSubmissionSummary?.userReviewSummary?.reviewType &&
        this.userPlaylistSubmissionSummary.userReviewSummary.reviewType !== 'AUTOMATIC'
          ? this.translocoService.translate('translations.dialog.title.formSubmissionReviewEnabled')
          : this.translocoService.translate('translations.dialog.title.formSubmission');
      this.dialogService.showConfirmDialog(dialogMessage, this.translocoService).then((confirmed) => {
        if (confirmed) {
          this.processSubmit();
        }
      });
    } else {
      this.processSubmit();
    }
  }

  processSubmit() {
    this.existingUserAnswer = true;
    if (ContentHelper.shouldDisplayReviewersDialogOnFormSubmission(this.userPlaylistSubmissionSummary)) {
      this.openReviewersDialog();
    } else {
      this.submitProject();
    }
  }

  submitProject(): void {
    this.submissionInProgress = true;
    this.dataService.submitForm(this.playlistUid, this.resourceUid, this.formData.uid).subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.refreshStateAndFinishSubmission();
      }
    });
  }

  private refreshStateAndFinishSubmission() {
    this.dataService
      .getCardWithProgress(
        this.isProjectResource,
        this.playlistUri,
        this.resourceUri,
        this.publisherUri,
        this.packageUri,
        this.pageUri,
        this.languageCode,
      )
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.store.dispatch(new RefreshStateAfterFormSubmission(value));
          this.store.dispatch(new RefreshUserPlaylistSubmissionSummary(this.playlistUid));
          this.finishSubmission();
        }
      });
  }

  private openReviewersDialog() {
    const dialogRef = this.dialog.open(SubmitPlaylistReviewDialogComponent, {
      width: '90vw',
      minWidth: '15.625rem',
      maxWidth: '46.875rem',
      maxHeight: '33.75rem',
      restoreFocus: true,
      position: {
        top: '10vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-mat-dialog',
      backdropClass: 'dialog-backdrop',
      data: {
        isProjectResource: this.isProjectResource,
        playlistUid: this.playlistUid,
        playlistTitle: this.playlistTitle,
        userPlaylistSubmissionSummary: this.userPlaylistSubmissionSummary,
        mode: 'FORM',
        resourceUid: this.resourceUid,
        formUid: this.formData.uid,
        playlistUri: this.playlistUri,
        resourceUri: this.resourceUri,
        publisherUri: this.publisherUri,
        packageUri: this.packageUri,
        pageUri: this.pageUri,
        languageCode: this.languageCode,
      },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.finishSubmission();
      }
    });
  }

  private finishSubmission() {
    this.submitted = true;
    this.submissionInProgress = false;
    this.checkSubmissionMode();
  }

  get isSubmissionDisabled(): boolean {
    return !this.existingUserAnswer || this.submissionInProgress;
  }

  private checkSubmissionMode(): void {
    if (!this.learnerFormAnswer) {
      if (!this.playlistUid || !this.resourceUid || !this.formData?.uid) {
        return;
      }
      if (!this.validationInProgress && !this.validationProcessed) {
        this.validationInProgress = true;
        this.dataService.validateFormUpdate(this.playlistUid, this.resourceUid, this.formData?.uid).subscribe(({ isSuccess, value }) => {
          if (isSuccess) {
            this.canEdit = value.canBeUpdated;
            this.canNotEditReason = ContentHelper.formCanNotBeEditedReason(this.translocoService, value);
            this.requiresResubmission = false;
            this.validationProcessed = true;
          }
          this.checkSubmissionLocked();
          this.cd.detectChanges();
          this.validationInProgress = false;
        });
      }
    } else {
      this.canEdit = this.learnerFormAnswer?.updatePermission?.canBeUpdated;
      this.canNotEditReason = ContentHelper.formCanNotBeEditedReason(this.translocoService, this.learnerFormAnswer?.updatePermission);
      this.requiresResubmission = this.learnerFormAnswer?.requiresResubmission;
      this.checkSubmissionLocked();
      this.cd.detectChanges();
    }
  }

  private checkSubmissionLocked() {
    if (this.userPlaylistSubmissionSummary?.submissionsLocked) {
      this.canEdit = false;
      this.canNotEditReason = this.translocoService.translate('translations.formUpdateDisabledReasons.submissionClosed');
      this.requiresResubmission = false;
    }
  }

  private loadUserAnswerProjectDetails() {
    if (this.projectDetailsFetchInProgress) return;

    this.projectDetailsFetchInProgress = true;
    if (this.userAnswer && (!this.projectDetails || this.projectDetails._id !== this.userAnswer)) {
      this.projectService.getProjectDetails(this.userAnswer, null).subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.projectDetails = value;
        }
        this.projectDetailsFetchInProgress = false;
        this.cd.detectChanges();
      });
    }
  }
}
